import env from 'env'
import md5 from 'blueimp-md5'

import type { Album, Artist, Image, Playlist, Show, Song, Venue, ReleaseProduct, Track, Chapter } from '../types'
import type {
  LegacyReleaseResponse,
  LegacyArtist,
  CatalogReleaseTypeC as SongItem,
  CatalogReleaseItem,
  LegacyProduct,
  CatalogReleaseTypeD,
  LegacyCatalogSearchContainer,
  LegacyCatalogSearchTypeContainers,
  LegacySearchResultItem,
  LegacyRecommendedRelease,
} from '../services/api/catalog'

import type { PlayerSrc } from '../components/players'
import type { StashReleaseItem } from '../services/api/stash'

import { diffInSecondsUntilDate, dateInRange, formatTime, formatToHumanTime, timeToDuration } from './time'

import defaultState, { isNormalized } from '../components/release'

import { sanitize } from '../utils/dom'
import { uuid } from '.'
import { CONTENT_SUBTYPE, VIDEO_PLAYER_SUPPORT_TYPE, VIDEO_PLAYER_TYPE } from '../constants/enums'

export const _config = { value: true, enumerable: false, writable: false }

export const enum SEARCH_CATALOG_MATCH_TYPE {
  ARTIST = 1,
  SONG = 2,
  VENUE_NAME = 3,
  VENUE_CITY= 4,
  PERFORMANCE_DATE = 5,
  ALBUM= 6,
}

const enum PF_TYPE {
  MP3 = 0,
  LOSSLESS = 1,
  HD = 2,
  VIDEO = 3,
  LIVEVIDEO = 4,
  MP4 = 5,
  VIDEOONDEMAND = 6,
  CD = 7,
  LIVEHDVIDEO = 8,
  HDVIDEOONDEMAND = 9,
  HDVIDEO = 10
}

const emptyVenue = {
  id: null,
  country: '',
  date: null,
  city: '',
  state: '',
  name: '',
  title: ''
}

// Filter functions
const filterByPurchasedLive = (product: LegacyProduct) => product.productStatusType === 1 && product.liveEventInfo?.isEventLive
const filterByLiveVideo = (product: LegacyProduct) => [4, 8].includes(+product.pfType)
const filterByLiveEvent = (product: LegacyProduct) =>
  dateInRange(product.liveEvent?.eventStartDateStr, product.liveEvent?.eventEndDateStr, product.liveEvent?.timeZoneToDisplay as never)

// Url normalizer
const fixAssetUrl = (url?: string) => url && typeof url === 'string' ? url.startsWith('http') ? url : env.LEGACY_CDN + url : null

// Normalizers
const normalizeLegacyArtist = (artist: LegacyArtist) => {
  const key = String(artist.artistNameNoThe).trim()[0]
  return {
    name: String(artist.artistName).trim(),
    id: artist.artistID,
    avatar: { url: artist.artistImage && !artist.artistImage.startsWith('/assets/ld/') ? fixAssetUrl(artist?.artistImage) : null },
    cover: { url: null },
    showCount: +artist.numShows || 0,
    albumCount: +artist.numAlbums || 0,
    key: key >= '0' && key <= '9' ? '#' : key
  } as Artist
}

const normalizeArtist = <T extends ArtistContentItem | CatalogReleaseTypeD>(data: T) => {
  const artist = (data?.type === 'artist' ? (<CatalogReleaseTypeD>data).data : data) as ArtistContentItem

  return {
    id: artist?.id,
    name: artist?.name,
    showCount: artist?.showCount,
    albumCount: artist?.albumCount,
    type: artist?.type || 'artist',
    key: artist?.abbreviation || String(artist?.name)[0] || '',
    avatar: {
      ...artist?.avatarImage,
      url: fixAssetUrl(artist?.avatarImage?.url as never)
    },
    cover: {
      ...artist?.coverImage,
      url: fixAssetUrl(typeof artist?.coverImage === 'string' ? artist.coverImage : artist?.coverImage?.url as never)
    }
  } as Artist
}

const normalizePlaylist = (playlist: PlaylistItem, tracks: PlaylistTrackItem[] = []): Playlist => ({
  id: playlist.id,
  createdAt: new Date(playlist.createdAt).toISOString(),
  name: playlist.name,
  cover: {
    url: playlist.imageUrl || null,
  },
  duration: (playlist.tracksTotalTimeSeconds || 0) as number,
  time: playlist.tracksTotalTime,
  total: formatToHumanTime(playlist.tracksTotalTimeSeconds || 0),
  length: Number(playlist.trackCount || tracks.length),
  tracks: Array.isArray(tracks) ? tracks.map(normalizePlaylistSong) : []
})

const normalizePlaylistSong = (item: PlaylistTrackItem, index: number): Song => ({
  id: item.trackId,
  index: index,
  position: index + 1,
  type: item.albumTitle ? 'album' : 'show',
  uuid: item.id,
  name: item.name,
  time: item.duration,
  sort: index,
  duration: typeof item.durationSeconds === 'number' ? item.durationSeconds : timeToDuration(item.duration
    .split(':')
    .map((segment, index) => {
      if (index === 0 && (segment === '00' || segment === '0')) return false
      return segment
    })
    .filter(Boolean)
    .join(':')),
  show: {
    id: item.releaseId,
    nextId: null,
    prevId: null,
    title: item.name,
    performancedAt: new Date(item.releaseDate).toISOString(),
  },
  album: {
    id: item.releaseId,
    title: item.albumTitle,
    releasedAt: new Date(item.releaseDate).toISOString(),
  },
  venue: item.albumTitle ? { ...emptyVenue } : Object.assign({}, item.venue, {
    id: null,
    title: item.venue?.title || item.venue?.name || '',
  }) as Venue,
  artist: {
    key: item.artist.abbreviation || String(item.artist.name)[0] || '',
    cover: {
      url: null
    },
    avatar: {
      url: null
    },
    id: item.artist.id as string,
    name: item.artist.name as string,
  },
  cover: {
    width: item.image?.width,
    height: item.image?.height,
    url: item.image?.url
  }
})

const normalizeSongItem = (song: SongItem, index: number): Song => ({
  id: song.id,
  index,
  position: index + 1,
  duration: song.duration || 0,
  name: song.title || song.release.artist?.name || '',
  sort: index,
  type: song.release.releaseType === 'show' ? 'show' : 'album',
  artist: {
    key: '',
    cover: {
      url: null
    },
    avatar: {
      url: null
    },
    id: song.release.artist?.id || '',
    name: song.release.artist?.name || '',
  },
  venue: song.release.releaseType === 'show' ? Object.assign({}, song.release.showDetails.venue) as Venue : { ...emptyVenue },
  show: {
    id: song.release?.id,
    nextId: null,
    prevId: null,
    performancedAt: song.release?.showDetails?.performanceDate ? new Date(song.release.showDetails.performanceDate).toISOString() : '',
    title: song.release?.showDetails?.venue?.title || song.release?.showDetails?.venue?.name || '',
  },
  album: {
    id: song.release?.id,
    title: song.release?.albumDetails?.albumTitle || '',
    releasedAt: song.release.albumDetails?.releaseDate ? new Date(song.release.albumDetails.releaseDate).toISOString() : '',
  },
  time: formatTime(song.duration || 0),
  cover: {
    url: song.release?.coverImage?.url || null,
  }
})


const normalizeLegacyRecommendation = (item: LegacyRecommendedRelease): Nullable<Partial<ReleaseProduct>> => {
  try {
    return {
      id: item.containerID,
      artist: {
        name: item.artistName,
        id: null
      },
      name: item.titleName,
      show: {
        id: item.containerID,
        performancedAt: item.containerType == 0 && item.performanceDateShort ? new Date(item.performanceDateShort).toISOString() : null,
        title: item.titleName,
      },
      album: {
        id: item.containerID,
        title: item.titleName,
        releasedAt: null
      },
      type: item.containerType ? CONTENT_SUBTYPE.ALBUM : CONTENT_SUBTYPE.SHOW,
      venue: item.containerType ? {
        id: null, city: '', state: '', date: null, name: '', title: ''
      } : {
        id: null,
        name: item.venueName,
        city: item.venueCity,
        state: item.venueState,
        title: item.venue
      },
      cover: {
        url: fixAssetUrl(item.picURL)
      },
      poster: {
        url: fixAssetUrl(item.picURL)
      }
    } as unknown as Partial<ReleaseProduct>
  } catch (e) {
    return null
  }
}

const normalizeImage = (image: string | null) => {
  const slash = String(image).startsWith('/') ? '' : '/'
  return {
    url: image && typeof image === 'string' ? `${env.LEGACY_CDN }${slash}${image.replace(/4K(?=\.)/, '')}` : null
  }
}

export function normalizeLegacyRelease(data: LegacyReleaseResponse): ReleaseProduct {
  if (!data) return defaultState as never as ReleaseProduct

  const normalizeVideoId = () => {
    if (data.svodskuID == 0 || data.svodskuID == null) {
      const skuIdA = products.find(item => [PF_TYPE.LIVEHDVIDEO].includes(item.pfType))?.skuID
      const skuIdB = list.find(item => [PF_TYPE.LIVEHDVIDEO, PF_TYPE.LIVEVIDEO].includes(item.pfType))?.skuID

      return (skuIdA || skuIdB) ?? 0
    } else return data.svodskuID
  }

  const list = Array.isArray(data.productFormatList) ? data.productFormatList : []
  const products = Array.isArray(data.products) ? data.products : []

  const isLivePurchased = products.some(filterByPurchasedLive)
  const isAnyPurchased = products.some(item => item.productStatusType === 1)
  const isFreeAudio = products.some(item => item.isFreeAudioLivestream === 1)

  const isLiveEvent = list.some(filterByLiveEvent)

  const stream = list.find(filterByLiveVideo)

  const isLive = isLivePurchased || isLiveEvent

  const date = data.performanceDate || data.releaseDate ? new Date(data.performanceDate || data.releaseDate).toISOString() : null
  const liveStartsAt = stream?.liveEvent.eventStartDateStr || null
  const liveEndsAt = stream?.liveEvent.eventEndDateStr || null
  const formats = list.filter(item => [PF_TYPE.MP3, PF_TYPE.LOSSLESS].includes(item.pfType))

  const isRecent = stream ? dateInRange(
    stream.liveEvent.eventEndDateStr,
    stream.salewindow.saleEndDateStr,
    stream.salewindow.timeZoneToDisplay as never) : false

  const streamType = list.map(item => {
    switch (true) {
      case [
        PF_TYPE.HD,
        PF_TYPE.VIDEO,
        PF_TYPE.LIVEVIDEO,
        PF_TYPE.MP4,
        PF_TYPE.VIDEOONDEMAND,
        PF_TYPE.LIVEHDVIDEO,
        PF_TYPE.HDVIDEOONDEMAND,
        PF_TYPE.HDVIDEO
      ].includes(item.pfType):
        return 'video'
      default:
        return 'audio'
    }
  })

  const [isLossless, isHD] = (() => {
    const isLossless = list.some(item => item.pfType === 1)
    const isHD = list.some(item => [
      PF_TYPE.HD,
      PF_TYPE.LIVEHDVIDEO,
      PF_TYPE.HDVIDEOONDEMAND,
      PF_TYPE.HDVIDEO
    ].includes(item.pfType))

    return [!!isLossless, !!isHD]
  })()

  const startsAt = liveStartsAt ? new Date(liveStartsAt).toISOString() : null

  const state = {
    id: data.containerID,
    uuid: uuid(),
    productId: normalizeVideoId(),
    type: data.containerType ? CONTENT_SUBTYPE.ALBUM : CONTENT_SUBTYPE.SHOW,
    name: data.containerInfo || data.title,
    artist: {
      key: '',
      id: data.artistID,
      name: data.artistName,
      cover: normalizeImage(null),
      avatar: normalizeImage(null)
    },
    album: {
      id: data.containerID,
      title: data.containerInfo || data.title,
      releasedAt: date as never,
      count: 0,
    },
    show: {
      id: data.containerID,
      nextId: data.nextContainerID || null,
      prevId: data.prevContainerID || null,
      title: data.containerInfo || data.title,
      performancedAt: date as never,
      count: 0
    },
    notes: Array.isArray(data.notes) ? [...data.notes].map(note => ({ id: note.noteID, text: sanitize(note.note || '') })) : [],
    recommendations: [...(data.recommendations || [])].map(normalizeLegacyRecommendation).filter(Boolean),
    reviews: {
      items: [...(data?.reviews?.items || [])].map(review => ({
        reviewer: String(review.reviewerName).trim(),
        id: review.reviewID,
        addedAt: new Date(review.reviewDate),
        status: +review.reviewStatus,
        text: sanitize(String(review.review).trim())
      })).filter(review => review.status === 1),
      total: data?.reviews?.totalRecords || 0
    },
    tracks: Array.isArray(data.tracks) ? data.tracks.map((track, index) => ({
      id: String(track?.trackID),
      index,
      songId: track?.songID,
      name: track?.songTitle || '',
      duration: +track.totalRunningTime || 0,
      position: index + 1,
      sort: index,
      time: formatTime(track.totalRunningTime),
      isSony360: !!track.reality360Product,
      order: index + 1,
      releaseId: data.containerID,
      date: date as string,
      set: track.setNum,
      disk: track.discNum,
      clip: track.clipURL,
      isHLS: (<Record<string, unknown>>track).transportMethod === 'http'
    } as Track)) : [],
    stream: {
      isPreordered: products.some(item => item.productStatusType === 3 || item.productStatusType === 5),
      isLive,
      isExclusive: !!stream?.isSubStreamOnly,
      isFree: (data.isInSubscriptionProgram === false && !isAnyPurchased) || isFreeAudio,
      isFreeAudio,
      isLossless,
      isHD,
      isRecent,
      status: String(data.activeState).toLowerCase(),
      type: streamType,
      timezone: list.find(filterByLiveVideo)?.liveEvent.timeZoneToDisplay || null,
      startsIn: diffInSecondsUntilDate(startsAt),
      startsAt: startsAt,
      endsAt: liveEndsAt ? new Date(liveEndsAt).toISOString() : null,
    },
    expiriesAt: null,
    cover: normalizeImage(data?.img?.url),
    poster: normalizeImage(data.extImage ? `images/shows/${data.extImage}` : data?.img?.url),
    addedAt: new Date().toISOString(),
    format: formats.length ? String(formats[0]?.formatStr) : undefined,
    isPurchased: false,
    isPreordered: false,
    isAudio: data.tracks?.length > 0,
    isBySubscription: !!data.isInSubscriptionProgram,
    isSVOD: data.svodskuID > 0 && !data.isInSubscriptionProgram,
    isVOD: data.svodskuID > 0 && !!data.isInSubscriptionProgram,
    isSony360: data.hasSony360VOD,
    duration: Number(data.totalContainerRunningTime) || 0,
    chapters: Array.isArray(data.videoChapters) ? [...data.videoChapters]
      .map((chapter, index) => ({ id: index, title: chapter.chaptername, marker: chapter.chapterSeconds } as Chapter)) : [],
    venue: {
      id: null,
      country: '',
      date,
      city: data.venueCity || '',
      state: data.venueState || '',
      name: data.venueName || data.venue || '',
      title: [data.venueName, data.venueCity, data.venueState]
        .map(value => value?.trim())
        .filter(Boolean).join(', ') || data.venue || '',
    }
  }

  Object.defineProperty(state, isNormalized, _config)

  return state as ReleaseProduct
}

function normalizeReleaseItem<T extends CatalogReleaseItem, K extends Record<string, unknown>>(release: T, data: K) {
  const image = { url: null }
  const venue = Object.assign({
    id: null, city: '', state: '', country: '', date: null, name: '', title: ''
  }, release?.venue || {}, release?.showDetails?.venue || {}) as Venue


  if (!venue.title) venue.title = release.albumTitle || release.albumDetails?.albumTitle || release.title || release.showDetails?.venue?.title || ''

  const avatar = { ...image }

  const artist = Object.assign({ id: null, name: null, cover: image, avatar }, release?.artist)

  const releaseDate = release?.albumReleaseDate || release?.albumDetails?.releaseDate
  const performanceDate = release?.performanceDate || release?.showDetails?.performanceDate

  const releasedAt = releaseDate ? new Date(releaseDate).toISOString() : null
  const performancedAt = performanceDate ? new Date(performanceDate).toISOString() : null
  const isPreordered = String(release?.status).toLowerCase() === 'preorder'
  const audioFormatTypes = release.audioFormatTypes ?? data?.audioFormatTypes ?? []
  const formats = Array.isArray(audioFormatTypes) ? audioFormatTypes.filter(item => ['mp3', 'lossless'].includes(item)) : []
  const isAudio = formats.length > 0

  const show = Object.assign({
    id: null, title: null, nextId: null, prevId: null, performancedAt: null, count: 0
  }, {
    id: release?.id,
    performancedAt,
    title: release.title || data['title' as never],
  }) as Show

  const album = Object.assign({
    id: null, title: null, releasedAt: null, count: 0
  }, {
    id: release?.id,
    title: release?.albumTitle || release?.albumDetails?.albumTitle || '',
    releasedAt,
  }) as Album

  const poster = {
    ...release?.image,
    url: fixAssetUrl((typeof release?.coverImage === 'string' ? release.coverImage : release?.coverImage?.url) || release.image?.url as never)
  }
  const cover = {
    ...release?.image,
    url: fixAssetUrl((release?.image?.url || release?.coverImage?.url) as never) || poster.url
  }

  const isBySubscription = Boolean(release.isInSubscriptionCatalog ?? release.isInSubscriptionProgram ?? data?.isInSubscriptionProgram)

  return Object.assign(Object.create(null), {
    tracks: [],
    notes: [],
    reviews: {
      items: [],
      total: 0
    },
    chapters: [],
    addedAt: release.addedAt ? new Date(release.addedAt).toISOString() : new Date().toISOString(),
    isBySubscription,
    isSVOD: data.type === CONTENT_SUBTYPE.SVOD,
    isVOD: release.hasVideoContent || release.hasVideoOnDemand || data.type === 'video',
    isPurchased: false,
    isPreordered: false,
    format: formats.length ? String(formats[formats.length - 1]).toLowerCase() : undefined,
    isAudio,
    id: data.id || release?.id,
    priority: data.priority || 0,
    artist,
    type: release.releaseType || data.type || (release.albumReleaseDate ? CONTENT_SUBTYPE.ALBUM : CONTENT_SUBTYPE.SHOW),
    name: data?.title || data?.songTitle || album?.title || release?.title,
    duration: release?.duration || data.duration || 0,
    show,
    album,
    venue,
    poster,
    cover,
    stream: {
      isLive: Array.isArray(release?.videoFormatTypes) ? release.videoFormatTypes.some(item => item === 'liveHdVideo') : false,
      isFree: !isBySubscription,
      isFreeAudio: false,
      isPreordered,
      isLossless: Array.isArray(release?.audioFormatTypes) ? release.audioFormatTypes.some(item => item === 'lossless') : false,
      isHD: Array.isArray(release?.audioFormatTypes) ? release.audioFormatTypes.some(item => item === 'hd') : false,
      isExclusive: false,
      status: release.status ? String(release.status).toLowerCase() : 'available',
      type: [Array.isArray(release?.videoFormatTypes) ? 'video' : false, Array.isArray(release?.audioFormatTypes) ? 'audio' : false].filter(Boolean),
      startsAt: null,
      startsIn: NaN,
      endsAt: null,
      timezone: 'ET',
    },
  }, data || {}) as unknown as ReleaseProduct
}

function normalizeStashItem(data: StashReleaseItem) {
  if (!data) return defaultState as never as ReleaseProduct

  const release = (data?.product ? data.product?.release : data) as unknown as CatalogReleaseItem
  const product = data?.product || {}
  const type = product?.format === 'hdVideoOnDemand' || release?.hasVideoOnDemand ? CONTENT_SUBTYPE.SVOD : (product?.format === 'liveHdStream' ? CONTENT_SUBTYPE.LIVESTREAM : release.type) as CONTENT_SUBTYPE
  const isPreordered = String(product.release?.status).toLowerCase() === 'preorder'
  const state: ReleaseProduct = normalizeReleaseItem(release, {
    uuid: data.id as never,
    id: release.id,
    status: data.status,
    format: product.format,
    content: product.contentType,
    name: product?.songTitle || product?.title || release?.venue?.title || release.albumTitle || release.title,
    type,
    productId: product?.trackId || product?.id || release?.id,
    orderId: data.orderId,
    stream: {
      isRecent: false,
      isPreordered,
      isLive: product?.isLiveEvent && dateInRange(product?.liveEventStartsAt, product?.liveEventEndsAt, product?.liveEventTimeZone as never),
      isFree: false,
      isFreeAudio: false,
      isLossless: false,
      isHD: product?.format === 'hdVideoOnDemand',
      isExclusive: false,
      status: data.status || 'available',
      type: [product.contentType],
      startsAt: product?.liveEventStartsAt || null,
      endsAt: product?.liveEventEndsAt || null,
      timezone: product?.liveEventTimeZone || null,
    },
    expiriesAt: data.accessExpiresAt ? new Date(data.accessExpiresAt).toISOString() : null,
    isPurchased: true,
    isPreordered,
    isBySubscription: Boolean(release.isInSubscriptionCatalog ?? release.isInSubscriptionProgram),
    isAudio: String(product.contentType).toLowerCase() === 'audio',
    isSVOD: type === CONTENT_SUBTYPE.SVOD,
    isVOD: release?.hasVideoOnDemand,
  })

  Object.defineProperty(state, isNormalized, { value: true, enumerable: false, writable: false })

  return state
}

export const normalizeCatalogSearchContainer = (containers: Array<LegacyCatalogSearchContainer>): Array<SearchResultItem> => {
  const normalize = (data: LegacySearchResultItem) => {
    if (!data) return defaultState as never as ReleaseProduct

    const state: ReleaseProduct = Object.assign(Object.create(null), defaultState, {
      id: data.containerID,
      uuid: uuid(),
      status: data.availabilityStr?.toLowerCase(),
      name: data.containerName,
      cover: normalizeImage(data?.img?.url as never),
      productId: data?.songID || data?.trackID || data.containerID,
      stream: undefined,
      isPurchased: false,
      isBySubscription: +data.availability === 1,
      isAudio: true,
      show: {
        id: data.containerID,
        performancedAt: data.performanceDate ? new Date(data.performanceDate).toISOString() : null,
        title: data.containerName,
      },
      album: {
        id: data.containerID,
        title: data.containerName,
      },
      artist: {
        id: data.artistID,
        name: data.artistName,
        cover: { url: null },
        avatar: { url: null }
      },
      venue: {
        id: null,
        country: '',
        date: data.performanceDate ? new Date(data.performanceDate).toISOString() : null,
        city: data.venueCity || '',
        state: data.venueState || '',
        name: data.venue || '',
        title: [data.venueName, data.venueCity, data.venueState]
          .filter(Boolean).join(', ') || data.venue || '',
      },
    })

    Object.defineProperty(state, isNormalized, { value: true, enumerable: false, writable: false })

    return state
  }

  return (containers || []).map<SearchResultItem>(item => {
    return {
      keyword: item.matchedStr,
      hidden: true,
      items: item.catalogSearchResultItems.map(normalize)
    }
  })
}

export const normalizeLegacySearch = (containers: Array<LegacyCatalogSearchTypeContainers>): SearchResult => {
  const result = {
    artists: [],
    songs: [],
    locations: [],
    venues: [],
    albums: [],
    date: []
  } as SearchResult

  if (!containers || containers?.length === 0) return result

  containers.forEach(item => {
    const data = normalizeCatalogSearchContainer(item.catalogSearchContainers)

    switch (item.matchType) {
      case SEARCH_CATALOG_MATCH_TYPE.ARTIST:
        result.artists = data
        break
      case SEARCH_CATALOG_MATCH_TYPE.SONG:
        result.songs = data
        break
      case SEARCH_CATALOG_MATCH_TYPE.VENUE_NAME:
        result.venues = data
        break
      case SEARCH_CATALOG_MATCH_TYPE.VENUE_CITY:
        result.locations = data
        break
      case SEARCH_CATALOG_MATCH_TYPE.PERFORMANCE_DATE:
        result.date = data
        break
      case SEARCH_CATALOG_MATCH_TYPE.ALBUM:
        result.albums = data
        break
    }
  })

  return result
}

function livephishLegacyParams() {
  const ONE_MINUTE = 60000
  const sTime = Date.now()
  const longTime = sTime + ONE_MINUTE
  const time = `${longTime}.000000`
  const token = 'jdfirj8475jf_' // ???

  return {
    tk: md5(token + time),
    lxp: time
  }
}

const videoPlayerMeta = (data: Streamlink | null | undefined, defaultPlayer?: VIDEO_PLAYER_TYPE): VideoPlayerMeta => {
  defaultPlayer = defaultPlayer || VIDEO_PLAYER_SUPPORT_TYPE.JWPLAYER

  const isValidPlayer: boolean = [
    VIDEO_PLAYER_SUPPORT_TYPE.JWPLAYER,
    VIDEO_PLAYER_SUPPORT_TYPE.BITMOVIN]
    .includes(data?.player as VIDEO_PLAYER_TYPE)

  const player = (isValidPlayer ? data?.player : defaultPlayer) as VIDEO_PLAYER_TYPE
  const src = (data?.dashStreamLink ? { dash: data?.dashStreamLink, hls: data?.streamLink } : data?.streamLink) as PlayerSrc

  return { player, src }
}

export {
  fixAssetUrl,
  normalizeLegacyArtist,
  normalizeArtist,
  normalizePlaylist,
  normalizePlaylistSong,
  normalizeSongItem,
  normalizeStashItem,
  normalizeReleaseItem,
  videoPlayerMeta,
  livephishLegacyParams,
}

/**
 * Api Types
 */

type PlaylistItem = {
  id: string | number;
  name: string;
  trackCount: number;
  tracksTotalTime: string;
  tracksTotalTimeSeconds: number;
  duration: number;
  imageUrl: string;
  createdAt: string;
}

type ArtistContentItem = {
  abbreviation: string
  id: string
  key: string,
  name: string,
  type: 'artist'
  showCount: number
  albumCount: number
  avatarImage?: Image
  coverImage?: Image
}

type PlaylistTrackItem = {
  id: string
  name: string
  trackId: string
  releaseId: string
  albumTitle: string
  durationSeconds: number
  clipUrl: string
  artist: Partial<ArtistContentItem>
  image: Image
  duration: string
  releaseDate: string
  venue: Nullable<Venue>
}

type SearchResultItem = {
  keyword: string,
  hidden?: boolean,
  items: ReleaseProduct[]
}

type SearchResult = {
  artists: Array<SearchResultItem>
  songs: Array<SearchResultItem>
  locations: Array<SearchResultItem>,
  venues: Array<SearchResultItem>
  albums: Array<SearchResultItem>,
  date: Array<SearchResultItem>,
}

type Streamlink = {
  dashStreamLink: Nullable<string>,
  drmSessionId: Nullable<string>,
  drmUserId: Nullable<string>,
  drmEnv: Nullable<string>,
  responseVersion: Nullable<string>,
  streamLink: Nullable<string>,
  player: Nullable<string>
}

type VideoPlayerMeta = {
  player: VIDEO_PLAYER_TYPE,
  src: PlayerSrc
}

export type {
  ArtistContentItem,
  PlaylistTrackItem,
  PlaylistItem,
  SongItem,
  SearchResult,
  SearchResultItem,
  Streamlink,
  // Legacy
  LegacyReleaseResponse,
  LegacyArtist,
}
